import { z } from "zod";

import { faultTypeSchema, shortCircuitCaseSchema } from "./faultContract";

// The controlled vocabularies of the Type 1 / Type 2 starter coordination module
// (EOS-05a, Master Prompt A20), copied verbatim from the backend so a value the
// API can send is never rejected here. Sources at commit 7a8aa33:
// backend/app/domain/electrical/starter_coordination/results.py
// (StarterCoordinationStatus, StarterCoordinationWarningCode and the four code
// sets) and .../starter_coordination/models.py (StartingMethod,
// StarterCoordinationType, ScpdKind, ProspectiveCurrentSource).
// starterCoordinationContract.test.ts pins each list.
//
// The calculation case and the fault type of the linked fault run are the fault
// module's own enums, so they are reused from faultContract rather than written
// out a second time.

export const starterCoordinationStatusSchema = z.enum([
  "VALID",
  "WARNING",
  "REVIEW_REQUIRED",
  "NO_SOLUTION",
]);

export const starterCoordinationWarningCodeSchema = z.enum([
  "IQ_BELOW_PROSPECTIVE_CURRENT",
  "DECLARED_TYPE_BELOW_REQUIRED",
  "TEST_VOLTAGE_BELOW_SYSTEM",
  "CONTACTOR_AC3_BELOW_FLC",
  "FLC_OUTSIDE_OVERLOAD_RANGE",
  "IQ_NOT_ESTABLISHED",
  "TEST_VOLTAGE_NOT_ESTABLISHED",
  "CERTIFICATE_REFERENCE_NOT_ESTABLISHED",
  "STARTER_CURRENT_CHECKS_NOT_EVALUATED",
  "PROSPECTIVE_CURRENT_NOT_LINKED",
  "FAULT_RUN_NOT_MAXIMUM_THREE_PHASE",
  "FAULT_RUN_BUS_MISMATCH",
  "COORDINATION_EVIDENCE_MANUFACTURER_DECLARED",
]);

export const startingMethodSchema = z.enum(["DOL", "STAR_DELTA", "SOFT_STARTER", "VFD"]);

export const starterCoordinationTypeSchema = z.enum(["TYPE_1", "TYPE_2"]);

export const scpdKindSchema = z.enum(["CIRCUIT_BREAKER", "FUSE"]);

export const prospectiveCurrentSourceSchema = z.enum(["LINKED_FAULT_RUN", "TYPED"]);

export { faultTypeSchema, shortCircuitCaseSchema };

/** A failed check: the starter as declared cannot be accepted (A20 (f)). */
export const NO_SOLUTION_WARNING_CODES = [
  "IQ_BELOW_PROSPECTIVE_CURRENT",
  "DECLARED_TYPE_BELOW_REQUIRED",
  "CONTACTOR_AC3_BELOW_FLC",
  "FLC_OUTSIDE_OVERLOAD_RANGE",
] as const;

/**
 * Evidence the engineer still owes: a certificate test voltage below the system voltage
 * (IR-001 F1: neither a pass nor a failure), missing certificate items, or an unlinked
 * fault level.
 */
export const REVIEW_REQUIRED_WARNING_CODES = [
  "TEST_VOLTAGE_BELOW_SYSTEM",
  "IQ_NOT_ESTABLISHED",
  "TEST_VOLTAGE_NOT_ESTABLISHED",
  "CERTIFICATE_REFERENCE_NOT_ESTABLISHED",
  "PROSPECTIVE_CURRENT_NOT_LINKED",
  "FAULT_RUN_NOT_MAXIMUM_THREE_PHASE",
  "FAULT_RUN_BUS_MISMATCH",
] as const;

/** The codes a starter row may carry. */
export const ROW_WARNING_CODES = [
  ...NO_SOLUTION_WARNING_CODES,
  "TEST_VOLTAGE_BELOW_SYSTEM",
  "IQ_NOT_ESTABLISHED",
  "TEST_VOLTAGE_NOT_ESTABLISHED",
  "CERTIFICATE_REFERENCE_NOT_ESTABLISHED",
  "STARTER_CURRENT_CHECKS_NOT_EVALUATED",
] as const;

/** The codes the study itself may carry. */
export const STUDY_WARNING_CODES = [
  "PROSPECTIVE_CURRENT_NOT_LINKED",
  "FAULT_RUN_NOT_MAXIMUM_THREE_PHASE",
  "FAULT_RUN_BUS_MISMATCH",
  "COORDINATION_EVIDENCE_MANUFACTURER_DECLARED",
] as const;

/**
 * Carried by every result (A20 (g), GAP-020): coordination is checked as
 * manufacturer-declared evidence, never proven by the engine. It gives WARNING,
 * never REVIEW_REQUIRED.
 */
export const COORDINATION_EVIDENCE_MANUFACTURER_DECLARED =
  "COORDINATION_EVIDENCE_MANUFACTURER_DECLARED";

/** The three certificate items that may be left blank; blank = NOT ESTABLISHED (A20 (e)). */
export const CERTIFICATE_ITEMS = [
  "certified_iq_ka",
  "test_voltage_v",
  "certificate_reference",
] as const;

export type StarterCoordinationStatus = z.infer<typeof starterCoordinationStatusSchema>;
export type StarterCoordinationWarningCode = z.infer<
  typeof starterCoordinationWarningCodeSchema
>;
export type StartingMethod = z.infer<typeof startingMethodSchema>;
export type StarterCoordinationType = z.infer<typeof starterCoordinationTypeSchema>;
export type ScpdKind = z.infer<typeof scpdKindSchema>;
export type ProspectiveCurrentSource = z.infer<typeof prospectiveCurrentSourceSchema>;
